import { createGlobalStyle, css } from 'styled-components';

import { Theme } from 'utils/color/horizonTheme';

/**
 * This is global css passed a s a child of ThemeProvider in
 * `src/horizon/index.tsx`. It should contain only global styles that
 * don't fit nicely as a style of a specific component, e.g. scrollbars.
 *
 * TODO: consider moving appropriate items from App.css here.
 */

export const GlobalStyles = createGlobalStyle`
  ${({ theme }) => css`
    /*
     * antd 4's base CSS set the body text, heading, link and selection colors and the base type
     * size until its last component import went (docs/antd5-migration-plan.md, 8.3). The
     * structural reset it also carried comes from antd/dist/reset.css, imported in App.jsx.
     */
    body {
      font-family: 'Inter', sans-serif !important;
      font-size: 14px;
      line-height: 1.5715;
      font-variant: tabular-nums;
      font-feature-settings: 'tnum';
      color: ${theme.textColor};
      background: ${theme.primaryBackgroundColor};
    }

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
      color: ${theme.headerColor};
    }

    a {
      color: ${theme.actionText};
      text-decoration: none;
      background-color: transparent;
      cursor: pointer;
      transition: color 0.3s;
    }

    a[disabled] {
      color: ${theme.disabledTextColor};
      cursor: not-allowed;
    }

    ::selection {
      color: ${theme.solidActionText};
      background: ${theme.teal};
    }

    /* Brand headlines use Open Sans; body copy stays Inter (Brand Guidelines V3). */
    h1,
    h2,
    h3,
    h4,
    h5,
    h6,
    .ant-typography h1,
    .ant-typography h2,
    .ant-typography h3,
    .ant-typography h4,
    .ant-typography h5,
    h1.ant-typography,
    h2.ant-typography,
    h3.ant-typography,
    h4.ant-typography,
    h5.ant-typography {
      font-family: 'Open Sans', sans-serif;
    }

    a:hover {
      color: ${theme.actionTextHover};
    }

    // Emphasized notes ("no comments yet", hints) read as muted text.
    em {
      color: ${theme.descriptionColor};
    }

    /*
     * antd 4's dropdown CSS, which stopped loading with its Table, shrank a trigger's down arrow
     * to 10px; no antd 5 token sizes it. Kept with antd 4's corners and header weights
     * (docs/antd5-migration-plan.md, 9.1).
     */
    .ant-dropdown-trigger > .anticon.anticon-down,
    .ant-dropdown-link > .anticon.anticon-down,
    .ant-dropdown-button > .anticon.anticon-down {
      font-size: 10px;
      vertical-align: baseline;
    }

    /* Override browser autofill styles to maintain theme colors */
    .ant-input:-webkit-autofill,
    .ant-input:-webkit-autofill:hover,
    .ant-input:-webkit-autofill:focus,
    .ant-input:-webkit-autofill:active,
    .ant-input-affix-wrapper > input:-webkit-autofill,
    .ant-input-affix-wrapper > input:-webkit-autofill:hover,
    .ant-input-affix-wrapper > input:-webkit-autofill:focus,
    .ant-input-affix-wrapper > input:-webkit-autofill:active {
      -webkit-box-shadow: 0 0 0 1000px ${theme.inputBackgroundColor} inset !important;
      -webkit-text-fill-color: ${theme.headerColor} !important;
      caret-color: ${theme.headerColor} !important;
      transition: background-color 5000s ease-in-out 0s;
    }

    .ant-input-group-addon > button {
      border-radius: unset !important;
      border-right-width: 0 !important;
    }

    .ant-checkbox {
      top: 0;
    }

    .ant-form-item-has-error {
      margin-bottom: 24px;
    }

    /* antd 5 pulls the next item up over the validation message; antd 4 kept the gap below it. */
    .ant-form-item-margin-offset {
      margin-bottom: 0 !important;
    }

    // Buttons inside menus render as plain items. Primary ones keep their fill: table filter
    // dropdowns render inside .ant-dropdown, and their OK button is primary.
    .ant-dropdown-menu,
    .ant-dropdown,
    .ant-menu,
    .ant-tabs-dropdown-menu {
      button:not(.ant-menu-root > button):not([role='switch']):not(.ant-btn-primary) {
        background-color: unset;
        border: none;
      }
    }

    .ant-popover {
      padding: 0 !important;
    }
    .ant-popover-inner {
      padding: 12px;
    }
    .ant-popover-inner-content {
      padding: unset;
    }
    .ant-popover-arrow {
      border: none;
    }

    // The app's link colors are under 3:1 on both themes' tooltip backgrounds, and no antd
    // token sets link colors inside a tooltip. Collapsed menus repeat the item's link as its
    // tooltip label, which reads as a label rather than a link.
    .ant-tooltip:not(.ant-menu-inline-collapsed-tooltip) .ant-tooltip-inner a,
    .ant-tooltip:not(.ant-menu-inline-collapsed-tooltip) .ant-tooltip-inner a:hover {
      color: ${theme.tooltipTextColor};
      text-decoration: underline;
    }

    .ant-notification-notice {
      margin-bottom: unset !important;
    }

    // Panels divide at the header instead of antd's line above and below each panel's content; no
    // token moves the divider.
    .ant-collapse {
      .ant-collapse-item {
        border: none;
      }

      .ant-collapse-item:last-of-type:not(.ant-collapse-item-active) {
        .ant-collapse-header {
          border-bottom: unset;
        }
      }

      .ant-collapse-header {
        border-bottom: 1px solid ${theme.borderColor};
      }

      .ant-collapse-content {
        border: none !important;
      }
    }

    // Sky Blue text fails contrast on white, so light checked options keep the label color.
    ${
      theme.themeName === Theme.Light
        ? css`
            .ant-radio-group:not(.ant-radio-group-solid)
              .ant-radio-button-wrapper-checked:not(.ant-radio-button-wrapper-disabled) {
              color: ${theme.headerColor};
            }
          `
        : ''
    }
  `}

  ${({ theme }) =>
    theme.themeName === Theme.Dark
      ? css`
          html,
          body {
            scrollbar-color: ${theme.borderColor} ${theme.tertiaryBackgroundColor};

            ::-webkit-scrollbar-track {
              background-color: ${theme.tertiaryBackgroundColor};
            }

            ::-webkit-scrollbar-thumb {
              background-color: ${theme.borderColor};
            }
          }
        `
      : ''}
`;
